import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import SupplierClassificationFields from '../../../components/SupplierClassificationFields';
import { supplierRequestService, getErrorMessage } from '../../../services/apiService';
import { approverNames, classificationOf, isClassified } from '../../../utils/classification';

/**
 * Approve or reject a supplier request. Approving creates the vendor invite, which is routed to
 * its approvers (User Management › Invite approvers); the supplier receives the registration link
 * only after that approval. The classification is the one the plant chose on the request form —
 * shown here so it can be corrected; a request raised before the form asked for it is classified here.
 */
const ApprovalDialog = ({ request, action, onClose, onComplete }) => {
  const [rejectionReason, setRejectionReason] = useState('');
  const [classification, setClassification] = useState(() => classificationOf(request));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [approvalSuccess, setApprovalSuccess] = useState(false);
  const [inviteInfo, setInviteInfo] = useState(null);
  const [updatedRequestData, setUpdatedRequestData] = useState(null);

  const isApprove = action === 'approve';
  const chosenByPlant = isClassified(request);
  const classificationComplete = isClassified(classification);

  const handleSubmit = async () => {
    if (!isApprove && !rejectionReason.trim()) {
      setError('Please provide a reason for rejection');
      return;
    }
    if (isApprove && !classificationComplete) {
      setError('Choose the nature of business, OEM/ODM and the supplier sub-category');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (isApprove) {
        const response = await supplierRequestService.approveRequest(request.id, classification);
        const responseData = response?.data || response;
        const updatedRequest = responseData?.data || responseData;
        const inviteData = updatedRequest?.invite || response?.data?.invite || response?.invite;

        const requestUpdate = { ...request, ...updatedRequest, status: 'APPROVED', invite: inviteData };
        setInviteInfo(inviteData || null);
        setApprovalSuccess(true);
        setUpdatedRequestData(requestUpdate);
        setLoading(false);
        return;
      }

      const response = await supplierRequestService.rejectRequest(request.id, rejectionReason);
      const responseData = response?.data || response;
      const updatedRequest = responseData?.data || responseData;
      onComplete({ ...request, ...updatedRequest, status: 'REJECTED', rejection_reason: rejectionReason });
    } catch (err) {
      console.error(`${action} failed:`, err);
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl border border-slate-200 max-w-lg w-full p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-slate-900">
            {isApprove ? 'Approve Request' : 'Reject Request'}
          </h2>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full p-2 transition-colors"
            aria-label="Close"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        {/* Request Info */}
        <div className="mb-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
          <p className="font-semibold text-slate-900 mb-1">{request.required_vendor_name}</p>
          <p className="text-sm text-slate-600">Requested by: {request.requested_by || request.user_name}</p>
          <p className="text-sm text-slate-600">Email: {request.vendor_email}</p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
            <Icon name="AlertCircle" size={18} className="text-red-600" />
            <span className="text-sm text-red-800">{error}</span>
          </div>
        )}

        {/* Rejection Reason */}
        {!isApprove && (
          <div className="mb-6">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Rejection Reason <span className="text-red-500">*</span>
            </label>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              rows={4}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              placeholder="Please provide a detailed reason for rejection..."
            />
          </div>
        )}

        {/* Classification + what happens next */}
        {isApprove && !approvalSuccess && (
          <div className="mb-6 space-y-4">
            <SupplierClassificationFields
              value={classification}
              onChange={(field, code) => setClassification((current) => ({ ...current, [field]: code }))}
              idPrefix="approve"
            />
            <p className="text-xs text-slate-500" data-testid="approve-classification-source">
              {chosenByPlant
                ? <>Chosen by <strong>{request.requested_by || request.user_name || 'the plant'}</strong> on the request. Change it here if it is not right.</>
                : 'This request was raised before the form asked for the classification, so choose it here.'}
            </p>
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <Icon name="Info" size={16} className="inline mr-2" />
                On approval a vendor invite is created and sent to the approver set up for this combination. The registration link
                goes to <strong>{request.vendor_email}</strong> once that approver approves it.
              </p>
            </div>
          </div>
        )}

        {/* Success */}
        {isApprove && approvalSuccess && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg" data-testid="request-approved-pending-invite">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="CheckCircle" size={20} className="text-green-600" />
              <span className="font-semibold text-slate-900">Request approved · invite sent for approval</span>
            </div>
            <p className="text-sm text-slate-800">
              {(inviteInfo?.approvers || []).length
                ? <>The vendor invite is waiting for <strong>{approverNames(inviteInfo.approvers)}</strong>{inviteInfo.approvers.length > 1 ? ' (any one of them can approve)' : ` (${inviteInfo.approvers[0].email})`}.</>
                : <>No approver is configured for this combination, so the invite is waiting for an administrator.</>}
              {' '}The registration link will be e-mailed to <strong>{request.vendor_email}</strong> once it is approved, and you will be told the decision.
            </p>
            {inviteInfo?.email_sent === false && (
              <p className="mt-2 text-xs text-amber-800">The approval request e-mail could not be sent{inviteInfo.email_error ? `: ${inviteInfo.email_error}` : ''}. The approver can still find it under Approvals › Invite Approval.</p>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3">
          <Button variant="outline" onClick={onClose} disabled={loading} className="flex-1">
            Cancel
          </Button>
          {approvalSuccess ? (
            <Button onClick={() => onComplete(updatedRequestData)} variant="success" className="flex-1" iconName="CheckCircle" iconPosition="left">
              Done
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={loading || (isApprove && !classificationComplete)}
              variant={isApprove ? 'success' : 'destructive'}
              className="flex-1"
              iconName={loading ? null : (isApprove ? 'CheckCircle' : 'XCircle')}
              iconPosition="left"
            >
              {loading ? (
                <>
                  <Icon name="Loader" size={18} className="animate-spin" />
                  Processing...
                </>
              ) : (
                isApprove ? 'Approve' : 'Reject'
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ApprovalDialog;
